export const WIZARD_TYPES = {
  SINGLE: 'SINGLE',
  SUBSCRIPTION: 'SUBSCRIPTION',
  CUSTOM: 'CUSTOM',
  TARGET: 'TARGET',
};

export const WIZARD_TYPE_LIST = [
  WIZARD_TYPES.SINGLE,
  WIZARD_TYPES.SUBSCRIPTION,
  WIZARD_TYPES.CUSTOM,
  WIZARD_TYPES.TARGET,
];

const BASE_PERIODS = [
  { Period: 'Daily', code: 'DAILY' },
  { Period: 'Daily + Saturday', code: 'DAILY_WITH_SATURDAYS' },
  { Period: 'Daily + Weekend', code: 'DAILY_WITH_WEEKENDS' },
  { Period: 'Weekly', code: 'WEEKLY' },
  { Period: 'Every 2 weeks', code: 'BIWEEKLY' },
  { Period: 'Monthly', code: 'MONTHLY' },
  { Period: 'Quarterly', code: 'QUARTERLY' },
  { Period: 'Every 6 months', code: 'HALFYEARLY' },
  { Period: 'Yearly', code: 'YEARLY' },
];

export const ONCE_PERIOD = { Period: 'Once', code: 'ONCE' };

export const getBillingPeriods = (wizardType, t) => {
  const periods = BASE_PERIODS.map((p) => ({
    ...p,
    Period: t(`plan.period_${p.code}`) !== `plan.period_${p.code}` ? t(`plan.period_${p.code}`) : p.Period,
  }));

  if (wizardType === WIZARD_TYPES.SINGLE) {
    return [{ ...ONCE_PERIOD, Period: t('plan.period_once') }];
  }

  if (wizardType === WIZARD_TYPES.SUBSCRIPTION || wizardType === WIZARD_TYPES.CUSTOM || wizardType === WIZARD_TYPES.TARGET) {
    return periods;
  }

  return [{ ...ONCE_PERIOD, Period: t('plan.period_once') }, ...periods];
};

export const getWizardConfig = (wizardType) => {
  switch (wizardType) {
    case WIZARD_TYPES.SINGLE:
      return {
        hasRemindersStep: false,
        excludeOnce: false,
        lockOnce: true,
        hidePeriod: true,
        hideDeductions: true,
        hideSetupFee: true,
        hideAutoBill: true,
        hideGraceReminder: true,
      };
    case WIZARD_TYPES.SUBSCRIPTION:
      return {
        hasRemindersStep: true,
        excludeOnce: true,
        lockOnce: false,
        hidePeriod: false,
        hideDeductions: false,
        hideSetupFee: false,
        hideAutoBill: false,
        hideGraceReminder: false,
      };
    case WIZARD_TYPES.CUSTOM:
      return {
        hasRemindersStep: true,
        excludeOnce: true,
        lockOnce: false,
        hidePeriod: false,
        hideDeductions: false,
        hideSetupFee: false,
        hideAutoBill: false,
        hideGraceReminder: false,
      };
    case WIZARD_TYPES.TARGET:
      return {
        hasRemindersStep: true,
        excludeOnce: true,
        lockOnce: false,
        hidePeriod: false,
        hideDeductions: false,
        hideSetupFee: true,
        hideAutoBill: true,
        hideGraceReminder: false,
      };
    default:
      return {
        hasRemindersStep: true,
        excludeOnce: false,
        lockOnce: false,
        hidePeriod: false,
        hideDeductions: false,
        hideSetupFee: false,
        hideAutoBill: false,
        hideGraceReminder: false,
      };
  }
};

export const shouldShowRemindersStep = (wizardType, billingPeriod) => {
  const config = getWizardConfig(wizardType);
  if (!config.hasRemindersStep) {
    return false;
  }
  return getRemindersDisabledReason(billingPeriod) === null;
};

export const getWizardSteps = (wizardType, billingPeriod = null) => {
  const steps = [
    { id: 'details', label: 'plan.wizard.step_details', icon: 'pi-file-edit' },
    { id: 'amount', label: 'plan.wizard.step_amount', icon: 'pi-wallet' },
  ];

  if (shouldShowRemindersStep(wizardType, billingPeriod)) {
    steps.push({ id: 'reminders', label: 'plan.wizard.step_reminders', icon: 'pi-bell' });
  }

  steps.push({ id: 'review', label: 'plan.wizard.step_review', icon: 'pi-check-circle' });
  return steps;
};

export const getWizardHeaderKey = (wizardType) => {
  switch (wizardType) {
    case WIZARD_TYPES.SINGLE:
      return 'plan.wizard.create_single';
    case WIZARD_TYPES.SUBSCRIPTION:
      return 'plan.wizard.create_subscription';
    case WIZARD_TYPES.CUSTOM:
      return 'plan.wizard.create_custom';
    case WIZARD_TYPES.TARGET:
      return 'plan.wizard.create_target';
    default:
      return 'plan.create_plan';
  }
};

export const getKindMenuItems = (t) =>
  WIZARD_TYPE_LIST.map((type) => ({
    wizardType: type,
    titleKey: `plan.wizard.${type.toLowerCase()}.title`,
    descriptionKey: `plan.wizard.${type.toLowerCase()}.description`,
    label: t(`plan.wizard.${type.toLowerCase()}.title`),
    description: t(`plan.wizard.${type.toLowerCase()}.description`),
  }));

export const buildPolicyOption = (code, t, isEnterprise = false) => {
  const map = {
    FLEXIBLE: { Policy: t('plan.flexible_billing'), code: 'FLEXIBLE', isFlexible: 'true' },
    FIXED: { Policy: t('plan.fixed_billing'), code: 'FIXED', isFlexible: 'false' },
    CUSTOM: { Policy: t('products.custom_billing'), code: 'CUSTOM' },
    SCHEDULE: { Policy: t('plan.schedule_billing'), code: 'SCHEDULE' },
    TARGET: { Policy: t('plan.target_billing'), code: 'TARGET' },
  };
  return map[code];
};

export const getBillingPeriodCode = (billingPeriod) => {
  if (!billingPeriod) {
    return null;
  }
  return billingPeriod.code ?? billingPeriod;
};

const SHORT_BILLING_PERIODS_FOR_REMINDERS = [
  'ONCE',
  'DAILY',
  'DAILY_WITH_SATURDAYS',
  'DAILY_WITH_WEEKENDS',
  'WEEKLY',
];

export const getRemindersDisabledReason = (billingPeriod) => {
  const code = getBillingPeriodCode(billingPeriod);
  if (!code) {
    return 'no_billing_period';
  }
  if (SHORT_BILLING_PERIODS_FOR_REMINDERS.includes(code)) {
    return 'unsupported_billing_period';
  }
  return null;
};

export const getBillingPeriodLabel = (billingPeriod, t) => {
  const code = getBillingPeriodCode(billingPeriod);
  if (!code) {
    return '';
  }
  const key = `products.period_${code}`;
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return billingPeriod?.Period || code;
};

export const canShowSetupFee = (wizardType, values) => {
  const config = getWizardConfig(wizardType);
  if (config.hideSetupFee || values.autoBilling) {
    return false;
  }

  if (getBillingPeriodCode(values.billingPeriod) === 'ONCE') {
    return false;
  }

  const policyCode = values.policy?.code;
  return Boolean(policyCode) && policyCode !== 'TARGET';
};

export const applyWizardPresets = (wizardType, formik, t, periodOptions) => {
  switch (wizardType) {
    case WIZARD_TYPES.SINGLE: {
      const oncePeriod = periodOptions.find((p) => p.code === 'ONCE') || { ...ONCE_PERIOD, Period: t('plan.period_once') };
      formik.setFieldValue('billingPeriod', oncePeriod);
      formik.setFieldValue('deductions', 1);
      formik.setFieldValue('feeCheck', false);
      formik.setFieldValue('setup', undefined);
      formik.setFieldValue('autoBilling', false);
      formik.setFieldValue('graceDays', undefined);
      formik.setFieldValue('reminderDays', undefined);
      break;
    }
    case WIZARD_TYPES.TARGET: {
      formik.setFieldValue('policy', buildPolicyOption('TARGET', t));
      formik.setFieldValue('targetEqualParts', true);
      formik.setFieldValue('recoverMissed', false);
      formik.setFieldValue('autoBilling', false);
      formik.setFieldValue('feeCheck', false);
      formik.setFieldValue('setup', undefined);
      break;
    }
    case WIZARD_TYPES.SUBSCRIPTION: {
      formik.setFieldValue('policy', null);
      formik.setFieldValue('cost', null);
      formik.setFieldValue('targetEqualParts', false);
      formik.setFieldValue('recoverMissed', false);
      formik.setFieldValue('billingPeriod', null);
      formik.setFieldValue('deductions', undefined);
      formik.setFieldValue('autoBilling', false);
      formik.setFieldValue('feeCheck', false);
      formik.setFieldValue('setup', undefined);
      break;
    }
    case WIZARD_TYPES.CUSTOM: {
      formik.setFieldValue('billingPeriod', null);
      formik.setFieldValue('deductions', undefined);
      formik.setFieldValue('autoBilling', false);
      formik.setFieldValue('policy', buildPolicyOption('CUSTOM', t));
      formik.setFieldValue('cost', null);
      formik.setFieldValue('feeCheck', false);
      formik.setFieldValue('setup', undefined);
      break;
    }
    default:
      break;
  }
};

export const navigateToPlanWizard = (navigate, groupId, { planGroup, wizardType, returnTo } = {}) => {
  navigate(`/products/plan/new/${groupId}`, {
    state: {
      planGroup,
      plan: Boolean(planGroup),
      ...(returnTo ? { returnTo } : {}),
      ...(wizardType ? { wizardType } : {}),
    },
  });
};
